/**
 * Ljudet till påskägget (se BatSignal.tsx).
 *
 * Tonerna byggs i webbläsaren med Web Audio i stället för att ligga som en
 * ljudfil i repot: det blir inget att ladda ner, inget att licensiera, och
 * ingenting som väger något i bygget. Det här är ett eget dramatiskt stick —
 * ingen inspelning och ingen melodi från någon film.
 *
 * Anropas alltid från en klickning, vilket är vad webbläsaren kräver för att
 * släppa fram ljud.
 */

type AudioWindow = Window & { webkitAudioContext?: typeof AudioContext };

/** Vitt brus som svep — "vingslaget". */
function whoosh(ctx: AudioContext, at: number) {
  const length = Math.floor(ctx.sampleRate * 1.1);
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < length; i++) {
    data[i] = (Math.random() * 2 - 1) * (1 - i / length);
  }

  const source = ctx.createBufferSource();
  source.buffer = buffer;

  const filter = ctx.createBiquadFilter();
  filter.type = "bandpass";
  filter.Q.value = 1.1;
  filter.frequency.setValueAtTime(320, at);
  filter.frequency.exponentialRampToValueAtTime(2600, at + 0.42);
  filter.frequency.exponentialRampToValueAtTime(240, at + 1.05);

  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0, at);
  gain.gain.linearRampToValueAtTime(0.5, at + 0.14);
  gain.gain.exponentialRampToValueAtTime(0.001, at + 1.1);

  source.connect(filter).connect(gain).connect(ctx.destination);
  source.start(at);
}

/** Låg svällande grund som ger tyngd. */
function swell(ctx: AudioContext, at: number) {
  const osc = ctx.createOscillator();
  osc.type = "sawtooth";
  osc.frequency.setValueAtTime(48, at);
  osc.frequency.exponentialRampToValueAtTime(96, at + 0.9);

  const filter = ctx.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.setValueAtTime(180, at);
  filter.frequency.linearRampToValueAtTime(900, at + 0.9);

  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0, at);
  gain.gain.linearRampToValueAtTime(0.32, at + 0.55);
  gain.gain.exponentialRampToValueAtTime(0.001, at + 1.5);

  osc.connect(filter).connect(gain).connect(ctx.destination);
  osc.start(at);
  osc.stop(at + 1.55);
}

/** Avslutande stöt — några otäcka intervall ovanpå varandra. */
function stab(ctx: AudioContext, at: number) {
  const master = ctx.createGain();
  master.gain.setValueAtTime(0.0001, at);
  master.gain.linearRampToValueAtTime(0.3, at + 0.02);
  master.gain.exponentialRampToValueAtTime(0.001, at + 1.2);
  master.connect(ctx.destination);

  // Tritonus plus oktav: spänt, utan att vara någon känd melodi.
  for (const frequency of [98, 138.6, 196, 277.2]) {
    const osc = ctx.createOscillator();
    osc.type = "square";
    osc.frequency.value = frequency;

    const gain = ctx.createGain();
    gain.gain.value = 0.25;

    osc.connect(gain).connect(master);
    osc.start(at);
    osc.stop(at + 1.2);
  }
}

export function playBatSting() {
  const Ctx = window.AudioContext ?? (window as AudioWindow).webkitAudioContext;
  if (!Ctx) return;

  let ctx: AudioContext;
  try {
    ctx = new Ctx();
  } catch {
    return;
  }

  const start = ctx.currentTime + 0.02;
  whoosh(ctx, start);
  swell(ctx, start);
  stab(ctx, start + 0.62);

  // Kontexten är en systemresurs och städas inte av sig själv.
  setTimeout(() => void ctx.close(), 2600);
}
